import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  Upload,
  Empty,
  Card,
  Space,
  Tooltip,
  Popconfirm,
  message,
  Modal,
  Spin,
  Tag,
  Progress,
} from 'antd';
import {
  UploadOutlined,
  DownloadOutlined,
  DeleteOutlined,
  EyeOutlined,
  PlusOutlined,
  FileOutlined,
  FilePdfOutlined,
  FileImageOutlined,
  FileWordOutlined,
  FileExcelOutlined,
  FileZipOutlined,
} from '@ant-design/icons';
import moment from 'moment-jalaali';
import { fetchAuthenticatedBlob } from '../../../utils/api';
import { getUserUploadsUrl } from '../../../utils/userUploadsUrl';
import {
  diffNewFiles,
  extractUploadedFiles,
  isConvertProgressComplete,
  parseConvertProgressPercent,
} from '../../../company/features';

const CONVERT_POLL_MS = 10000;

const WorkFilePanel = ({
  files = [],
  onUpload,
  onDelete,
  getDownloadUrl,
  accept,
  multiple = true,
  uploadLabel = 'آپلود فایل',
  /** چاپروم: قبل از آپلود سوال تبدیل */
  askNeedConvert = false,
  /** (fileId) => Promise — وضعیت تبدیل یک فایل */
  getConvertProgress,
  /** بعد از اتمام تبدیل همه فایل‌ها */
  onConvertComplete,
}) => {
  const [uploading, setUploading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [convertModalOpen, setConvertModalOpen] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [pendingFiles, setPendingFiles] = useState(null);
  /** [{ id, name, percent, done, error }] */
  const [convertItems, setConvertItems] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(null);
  const pollRef = useRef(null);
  const convertItemsRef = useRef([]);

  convertItemsRef.current = convertItems;

  const stopConvertPoll = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  useEffect(() => () => stopConvertPoll(), []);

  const pollConvertProgress = async () => {
    if (typeof getConvertProgress !== 'function') return;
    const current = convertItemsRef.current;
    if (!current.length) {
      stopConvertPoll();
      return;
    }

    const pending = current.filter((item) => !item.done && !item.error);
    if (!pending.length) {
      stopConvertPoll();
      return;
    }

    const updates = await Promise.all(
      pending.map(async (item) => {
        try {
          const res = await getConvertProgress(item.id);
          const percent = parseConvertProgressPercent(res);
          const done = isConvertProgressComplete(res, percent);
          return {
            id: item.id,
            percent: percent != null ? percent : item.percent,
            done,
            error: false,
          };
        } catch {
          return { id: item.id, percent: item.percent, done: false, error: true };
        }
      }),
    );

    const next = current.map((item) => {
      const u = updates.find((x) => String(x.id) === String(item.id));
      if (!u) return item;
      return {
        ...item,
        percent: u.percent ?? item.percent,
        done: Boolean(u.done),
        error: Boolean(u.error),
      };
    });

    convertItemsRef.current = next;
    setConvertItems(next);

    const allSettled = next.every((x) => x.done || x.error);
    if (allSettled) {
      stopConvertPoll();
      if (next.some((x) => x.done)) {
        message.success('تبدیل فایل‌ها کامل شد');
        onConvertComplete?.();
      }
      setTimeout(() => {
        setConvertItems([]);
        convertItemsRef.current = [];
      }, 2500);
    }
  };

  const startConvertPolling = (uploadedFiles) => {
    if (typeof getConvertProgress !== 'function' || !uploadedFiles?.length) return;
    stopConvertPoll();
    const items = uploadedFiles.map((f) => ({
      id: f.id,
      name: f.name || `فایل ${f.id}`,
      percent: 0,
      done: false,
      error: false,
    }));
    setConvertItems(items);
    convertItemsRef.current = items;
    // فوری یک بار + سپس هر ۱۰ ثانیه
    setTimeout(() => {
      pollConvertProgress();
      pollRef.current = setInterval(pollConvertProgress, CONVERT_POLL_MS);
    }, 0);
  };

  const isAbsoluteHttpUrl = (value) => /^https?:\/\//i.test(String(value || '').trim());

  const formatFileSize = (size) => {
    if (size == null || size === '') return null;
    const n = Number(String(size).replace(/,/g, '').trim());
    if (!Number.isFinite(n) || n < 0) return String(size);
    // بک‌اند حجم فایل وظیفه را معمولاً بر حسب MB می‌فرستد (مثل "2.65")
    if (n >= 1024) {
      return `${(n / 1024).toLocaleString('fa-IR', { maximumFractionDigits: 2 })} GB`;
    }
    if (n >= 1) {
      return `${n.toLocaleString('fa-IR', { maximumFractionDigits: 2 })} MB`;
    }
    if (n > 0) {
      return `${Math.round(n * 1024).toLocaleString('fa-IR')} KB`;
    }
    return '۰ MB';
  };

  const getFileExtension = (file) => {
    const filename = file.filename || file.name || file.hashname || '';
    return filename.split('.').pop()?.toLowerCase() || '';
  };

  const isImageFile = (file) =>
    ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(getFileExtension(file));
  const isPdfFile = (file) => getFileExtension(file) === 'pdf';
  const isVideoFile = (file) =>
    ['mp4', 'webm', 'ogg', 'mov', 'avi'].includes(getFileExtension(file));
  const isAudioFile = (file) =>
    ['mp3', 'wav', 'ogg', 'aac', 'flac'].includes(getFileExtension(file));
  const isPreviewable = (file) =>
    isImageFile(file) || isPdfFile(file) || isVideoFile(file) || isAudioFile(file);

  const getFileIcon = (file) => {
    const ext = getFileExtension(file);
    if (isImageFile(file)) return <FileImageOutlined className="text-lg" style={{ color: '#52c41a' }} />;
    if (isPdfFile(file)) return <FilePdfOutlined className="text-lg" style={{ color: '#ff4d4f' }} />;
    if (['doc', 'docx'].includes(ext))
      return <FileWordOutlined className="text-lg" style={{ color: '#1890ff' }} />;
    if (['xls', 'xlsx', 'csv'].includes(ext))
      return <FileExcelOutlined className="text-lg" style={{ color: '#52c41a' }} />;
    if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext))
      return <FileZipOutlined className="text-lg" style={{ color: '#faad14' }} />;
    return <FileOutlined className="text-lg" style={{ color: '#1890ff' }} />;
  };

  const getFileUrl = (file) => {
    // لینک مطلق خارجی (مثل pyapi.chaproom.com) را دست‌کاری نکن
    const absoluteCandidates = [
      file.file_url,
      file.external_link,
      file.url,
      file.download_url,
      file.file,
    ];
    for (const candidate of absoluteCandidates) {
      if (isAbsoluteHttpUrl(candidate)) return String(candidate).trim();
    }

    // مسیر نسبی user-uploads
    if (file.file) {
      const relative = String(file.file).replace(/^\/+/, '');
      const path = relative.startsWith('user-uploads/')
        ? `/${relative}`
        : `/user-uploads/${relative}`;
      return getUserUploadsUrl(path);
    }
    if (file.hashname && file.task_id) {
      return getUserUploadsUrl(`/user-uploads/task-files/${file.task_id}/${file.hashname}`);
    }
    if (file.hashname && (file.sub_task_id || file.subtask_id)) {
      const sid = file.sub_task_id || file.subtask_id;
      return getUserUploadsUrl(`/user-uploads/sub-task-files/${sid}/${file.hashname}`);
    }
    if (file.hashname && file.contract_id) {
      return getUserUploadsUrl(`/user-uploads/contract-files/${file.contract_id}/${file.hashname}`);
    }
    if (file.hashname && file.project_id) {
      return getUserUploadsUrl(`/user-uploads/project-files/${file.project_id}/${file.hashname}`);
    }
    return null;
  };

  const downloadFromDirectUrl = async (fileUrl, filename) => {
    // لینک خارجی: مستقیم باز کن (fetch معمولاً CORS می‌خورد)
    if (isAbsoluteHttpUrl(fileUrl)) {
      try {
        const host = new URL(fileUrl).host;
        if (host && host !== window.location.host) {
          window.open(fileUrl, '_blank', 'noopener,noreferrer');
          return;
        }
      } catch {
        window.open(fileUrl, '_blank', 'noopener,noreferrer');
        return;
      }
    }

    let blob;
    try {
      blob = await fetchAuthenticatedBlob(fileUrl);
    } catch {
      const response = await fetch(fileUrl);
      if (!response.ok) throw new Error(`خطا در دریافت فایل (${response.status})`);
      blob = await response.blob();
    }
    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(objectUrl);
  };

  const downloadFromAuthUrl = async (url, filename) => {
    const blob = await fetchAuthenticatedBlob(url);
    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(objectUrl);
  };

  const handleDownload = async (file) => {
    const filename = file.filename || file.name || file.hashname || `file-${file.id}`;
    const directUrl = getFileUrl(file);
    try {
      if (directUrl) {
        try {
          await downloadFromDirectUrl(directUrl, filename);
        } catch {
          window.open(directUrl, '_blank', 'noopener,noreferrer');
        }
        message.success('فایل با موفقیت دانلود شد');
        return;
      }
      const authUrl = typeof getDownloadUrl === 'function' ? getDownloadUrl(file.id) : null;
      if (!authUrl) {
        message.error('آدرس دانلود در دسترس نیست');
        return;
      }
      await downloadFromAuthUrl(authUrl, filename);
      message.success('فایل با موفقیت دانلود شد');
    } catch (error) {
      console.error('Error downloading file:', error);
      message.error('خطا در دانلود فایل');
    }
  };

  /** لینک pyapi را به مسیر پروکسی same-origin تبدیل می‌کند (بدون CORS) */
  const toChaproomProxyUrl = (absoluteUrl) => {
    try {
      const u = new URL(absoluteUrl);
      if (u.host !== 'pyapi.chaproom.com') return null;
      return `/chaproom-files${u.pathname}${u.search}`;
    } catch {
      return null;
    }
  };

  const isCrossOriginAbsolute = (url) => {
    if (!isAbsoluteHttpUrl(url)) return false;
    try {
      return new URL(url).origin !== window.location.origin;
    } catch {
      return true;
    }
  };

  const blobToPreviewObjectUrl = (blob, file) => {
    if (!blob || blob.size < 32) throw new Error('empty preview blob');
    if (blob.type && (blob.type.includes('text/html') || blob.type.includes('application/json'))) {
      throw new Error('preview returned non-file content');
    }
    if (isPdfFile(file) && (!blob.type || blob.type === 'application/octet-stream')) {
      return window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
    }
    if (isImageFile(file) && (!blob.type || blob.type === 'application/octet-stream')) {
      const ext = getFileExtension(file) || 'png';
      return window.URL.createObjectURL(new Blob([blob], { type: `image/${ext === 'jpg' ? 'jpeg' : ext}` }));
    }
    return window.URL.createObjectURL(blob);
  };

  const loadPreviewBlobUrl = async (file) => {
    const authUrl = typeof getDownloadUrl === 'function' ? getDownloadUrl(file.id) : null;
    if (!authUrl) throw new Error('no download url');
    const token = localStorage.getItem('authToken');
    // redirect:manual تا اگر API به pyapi ریدایرکت کند، CORS نخوریم
    const response = await fetch(authUrl, {
      method: 'GET',
      redirect: 'manual',
      headers: {
        Accept: '*/*',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
    if (response.type === 'opaqueredirect' || (response.status >= 300 && response.status < 400)) {
      throw new Error('download redirects externally');
    }
    if (!response.ok) throw new Error(`preview auth failed (${response.status})`);
    return blobToPreviewObjectUrl(await response.blob(), file);
  };

  const loadSameOriginBlobUrl = async (fileUrl, file) => {
    const response = await fetch(fileUrl, { method: 'GET' });
    if (!response.ok) throw new Error(`preview fetch failed (${response.status})`);
    return blobToPreviewObjectUrl(await response.blob(), file);
  };

  const handlePreview = async (file) => {
    setPreviewFile(file);
    setPreviewVisible(true);
    setPreviewLoading(true);
    setPreviewUrl(null);
    try {
      if (!isPreviewable(file)) {
        message.warning('پیش‌نمایش برای این نوع فایل پشتیبانی نمی‌شود');
        setPreviewVisible(false);
        return;
      }

      const directUrl = getFileUrl(file);
      const external = directUrl && isCrossOriginAbsolute(directUrl);

      // ۱) دانلود با توکن از API (ریدایرکت به pyapi را دنبال نکن → بدون CORS)
      if (file.id && typeof getDownloadUrl === 'function') {
        try {
          setPreviewUrl(await loadPreviewBlobUrl(file));
          return;
        } catch (authPreviewError) {
          console.warn('Auth preview failed, falling back:', authPreviewError);
        }
      }

      if (!directUrl) {
        message.warning('پیش‌نمایش برای این فایل در دسترس نیست');
        setPreviewVisible(false);
        return;
      }

      // ۲) لینک خارجی (pyapi): هرگز مستقیم fetch نکن (CORS).
      // از پروکسی same-origin blob بساز تا Content-Disposition: attachment هم بی‌اثر شود.
      if (external) {
        const proxyUrl = toChaproomProxyUrl(directUrl);
        if (proxyUrl) {
          try {
            setPreviewUrl(await loadSameOriginBlobUrl(proxyUrl, file));
            return;
          } catch (proxyError) {
            console.warn('Chaproom proxy preview failed:', proxyError);
          }
        }

        if (isImageFile(file) || isVideoFile(file) || isAudioFile(file)) {
          setPreviewUrl(directUrl);
          return;
        }

        // PDF: بدون fetch در iframe (اگر خالی بود از «باز کردن در تب جدید» استفاده شود)
        if (isPdfFile(file)) {
          setPreviewUrl(directUrl);
          return;
        }

        message.info('پیش‌نمایش داخل پنل ممکن نیست؛ فایل در تب جدید باز شد');
        window.open(directUrl, '_blank', 'noopener,noreferrer');
        setPreviewVisible(false);
        return;
      }

      // ۳) مسیر داخلی user-uploads
      try {
        const blob = await fetchAuthenticatedBlob(directUrl);
        setPreviewUrl(blobToPreviewObjectUrl(blob, file));
      } catch {
        setPreviewUrl(directUrl);
      }
    } catch (error) {
      console.error('Error loading preview:', error);
      message.error('خطا در بارگذاری پیش‌نمایش');
      setPreviewVisible(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleClosePreview = () => {
    if (previewUrl && previewUrl.startsWith('blob:')) {
      window.URL.revokeObjectURL(previewUrl);
    }
    setPreviewVisible(false);
    setPreviewFile(null);
    setPreviewUrl(null);
  };

  const runUpload = async (filesToUpload, needConvert) => {
    if (!filesToUpload?.length) return;

    setConvertModalOpen(false);
    setUploading(true);
    setUploadModalOpen(true);
    setUploadProgress({
      index: 0,
      total: filesToUpload.length,
      percent: 0,
      fileName: filesToUpload[0]?.name || 'فایل',
    });

    try {
      const options = needConvert ? { needConvert: true } : {};
      const beforeFiles = Array.isArray(files) ? files : [];
      const uploadRes = await onUpload(filesToUpload, {
        ...options,
        onProgress: (info) => setUploadProgress(info),
      });
      setUploadProgress((prev) =>
        prev ? { ...prev, percent: 100 } : { index: 0, total: 1, percent: 100, fileName: '' },
      );
      message.success('فایل‌ها با موفقیت آپلود شدند');
      setFileList([]);
      if (needConvert) {
        // اولویت: file_id از ریسپانس chunk-upload، بعد فایل‌های تازه از GET
        let uploaded = extractUploadedFiles(uploadRes);
        if (!uploaded.length && Array.isArray(uploadRes?.__newFiles) && uploadRes.__newFiles.length) {
          uploaded = extractUploadedFiles({ __newFiles: uploadRes.__newFiles });
        }
        if (!uploaded.length && Array.isArray(uploadRes?.responses)) {
          uploaded = uploadRes.responses.flatMap((r) => extractUploadedFiles(r));
        }
        if (!uploaded.length) {
          const maybeAfter = uploadRes?.__afterFiles || [];
          uploaded = extractUploadedFiles({
            __newFiles: diffNewFiles(beforeFiles, maybeAfter, filesToUpload.length),
          });
        }
        if (uploaded.length) {
          startConvertPolling(uploaded);
        } else {
          message.warning('آیدی فایل برای پیگیری تبدیل یافت نشد');
        }
      }
    } catch (error) {
      message.error(error?.message || 'خطا در آپلود فایل‌ها');
    } finally {
      setUploading(false);
      setPendingFiles(null);
      setTimeout(() => {
        setUploadModalOpen(false);
        setUploadProgress(null);
      }, 500);
    }
  };

  const handleUpload = async () => {
    if (!fileList.length) {
      message.warning('لطفاً حداقل یک فایل انتخاب کنید');
      return;
    }
    const filesToUpload = fileList.map((item) => item.originFileObj || item).filter(Boolean);
    if (askNeedConvert) {
      setPendingFiles(filesToUpload);
      setConvertModalOpen(true);
      return;
    }
    await runUpload(filesToUpload, false);
  };

  const uploadProps = {
    fileList,
    onChange: ({ fileList: next }) => setFileList(next),
    beforeUpload: () => false,
    multiple,
    accept,
  };

  const renderUploadDropArea = (emptyState = false) => (
    <Upload.Dragger
      {...uploadProps}
      showUploadList={false}
      openFileDialogOnClick
      className={emptyState ? 'bg-white dark:bg-gray-900/20' : ''}
      style={emptyState ? { borderRadius: 12 } : undefined}
    >
      <div className="flex flex-col items-center justify-center gap-2 py-2">
        <UploadOutlined className="text-teal-600 dark:text-teal-400 text-lg" />
        <span className="text-sm text-gray-700 dark:text-gray-300" style={{ fontFamily: 'CustomFont' }}>
          {fileList.length > 0 ? `${fileList.length} فایل انتخاب شده` : 'انتخاب یا رها کردن فایل'}
        </span>
        {emptyState ? (
          <span className="text-xs text-gray-500 dark:text-gray-400" style={{ fontFamily: 'CustomFont' }}>
            هنوز فایلی ثبت نشده؛ فایل را همین‌جا رها کنید
          </span>
        ) : null}
      </div>
    </Upload.Dragger>
  );

  const renderPreviewContent = () => {
    if (previewLoading) {
      return (
        <div className="flex items-center justify-center" style={{ minHeight: 300 }}>
          <Spin size="large" tip={<span style={{ fontFamily: 'CustomFont' }}>در حال بارگذاری...</span>} />
        </div>
      );
    }
    if (!previewUrl || !previewFile) return null;

    if (isImageFile(previewFile)) {
      return (
        <div className="flex items-center justify-center" style={{ maxHeight: '70vh', overflow: 'auto' }}>
          <img
            src={previewUrl}
            alt={previewFile.filename || previewFile.name}
            style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain', borderRadius: 8 }}
          />
        </div>
      );
    }
    if (isPdfFile(previewFile)) {
      return (
        <iframe
          src={previewUrl}
          title={previewFile.filename || previewFile.name}
          style={{ width: '100%', height: '70vh', border: 'none', borderRadius: 8 }}
        />
      );
    }
    if (isVideoFile(previewFile)) {
      return (
        <div className="flex items-center justify-center">
          <video src={previewUrl} controls style={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: 8 }} />
        </div>
      );
    }
    if (isAudioFile(previewFile)) {
      return (
        <div className="flex items-center justify-center py-12">
          <audio src={previewUrl} controls style={{ width: '100%' }} />
        </div>
      );
    }
    return (
      <div className="text-center py-12">
        <FileOutlined style={{ fontSize: 64, color: '#ccc' }} />
        <p className="mt-4 text-gray-500" style={{ fontFamily: 'CustomFont' }}>
          پیش‌نمایش برای این نوع فایل پشتیبانی نمی‌شود.
        </p>
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          onClick={() => handleDownload(previewFile)}
          className="mt-2"
          style={{ fontFamily: 'CustomFont' }}
        >
          دانلود فایل
        </Button>
      </div>
    );
  };

  return (
    <div className="space-y-4" style={{ fontFamily: 'CustomFont' }}>
      {convertItems.length > 0 ? (
        <div className="space-y-2 rounded-xl border border-indigo-100 dark:border-indigo-900/50 bg-indigo-50/70 dark:bg-indigo-950/30 px-4 py-3">
          <div className="text-sm font-medium text-indigo-800 dark:text-indigo-200 mb-1">
            وضعیت تبدیل فایل‌ها
          </div>
          {convertItems.map((item) => (
            <div key={item.id} className="rounded-lg bg-white/70 dark:bg-gray-900/40 px-3 py-2">
              <div className="flex items-center justify-between gap-2 mb-1 text-xs text-gray-700 dark:text-gray-200">
                <span className="truncate" title={item.name}>
                  {item.name}
                </span>
                <span className="font-semibold tabular-nums shrink-0">
                  {item.error
                    ? 'خطا'
                    : item.done
                      ? '۱۰۰٪'
                      : item.percent != null
                        ? `${item.percent}٪`
                        : '—'}
                </span>
              </div>
              <Progress
                percent={item.error ? 0 : item.percent ?? 0}
                size="small"
                status={item.error ? 'exception' : item.done ? 'success' : 'active'}
                strokeColor={item.error ? undefined : { from: '#6366f1', to: '#22c55e' }}
              />
            </div>
          ))}
        </div>
      ) : null}

      {uploadProgress && !uploadModalOpen ? (
        <div className="mb-4 rounded-xl border border-teal-100 dark:border-teal-900/40 bg-teal-50/50 dark:bg-teal-950/20 px-4 py-3">
          <div className="flex items-center justify-between gap-2 mb-1 text-xs text-gray-700 dark:text-gray-200">
            <span style={{ fontFamily: 'CustomFont' }}>
              در حال آپلود
              {uploadProgress.total > 1
                ? ` (${uploadProgress.index + 1} از ${uploadProgress.total})`
                : ''}
            </span>
            <span className="font-semibold tabular-nums shrink-0">
              {uploadProgress.percent != null ? `${uploadProgress.percent}٪` : '—'}
            </span>
          </div>
          <Progress
            percent={uploadProgress.percent ?? 0}
            showInfo={false}
            size="small"
            status="active"
            strokeColor={{ from: '#14b8a6', to: '#22c55e' }}
          />
          {uploadProgress.fileName ? (
            <div
              className="mt-1 text-xs text-gray-500 dark:text-gray-400 truncate"
              title={uploadProgress.fileName}
              style={{ fontFamily: 'CustomFont' }}
            >
              {uploadProgress.fileName}
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="flex flex-wrap items-center justify-end gap-3 mb-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="min-w-[230px]">{renderUploadDropArea()}</div>
          {fileList.length > 0 ? (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleUpload}
              loading={uploading}
              style={{ fontFamily: 'CustomFont' }}
              className="bg-green-600 hover:bg-green-700 border-0"
            >
              {uploadLabel} ({fileList.length})
            </Button>
          ) : null}
        </div>
      </div>

      {fileList.length > 0 ? (
        <div className="mb-4 flex flex-wrap gap-2">
          {fileList.map((f) => (
            <Tag
              key={f.uid}
              closable
              onClose={() => setFileList((prev) => prev.filter((x) => x.uid !== f.uid))}
            >
              {f.name}
            </Tag>
          ))}
        </div>
      ) : null}

      {!files?.length ? (
        <div className="py-8">
          {renderUploadDropArea(true)}
          <div className="mt-4">
            <Empty description="هیچ فایلی ثبت نشده است" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {files.map((file) => (
            <Card
              key={file.id}
              className="shadow-sm hover:shadow-md transition-all duration-200 border border-gray-200 dark:border-gray-700"
              style={{ fontFamily: 'CustomFont' }}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className="w-10 h-10 rounded-xl bg-teal-50 dark:bg-teal-900/20 flex items-center justify-center flex-shrink-0">
                    {getFileIcon(file)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3
                      className="text-base font-semibold text-gray-800 dark:text-gray-200 m-0 mb-1 truncate"
                      style={{ fontFamily: 'CustomFont' }}
                    >
                      {file.filename || file.name || 'فایل بدون نام'}
                    </h3>
                    <div className="flex items-center gap-4 flex-wrap text-xs text-gray-500 dark:text-gray-400">
                      {file.size ? (
                        <span style={{ fontFamily: 'CustomFont' }}>
                          حجم: {formatFileSize(file.size)}
                        </span>
                      ) : null}
                      {file.created_at ? (
                        <span style={{ fontFamily: 'CustomFont' }}>
                          {moment(file.created_at).format('jYYYY/jMM/jDD HH:mm')}
                        </span>
                      ) : null}
                      {getFileExtension(file) ? (
                        <span
                          className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300"
                          style={{ fontFamily: 'CustomFont' }}
                        >
                          {getFileExtension(file).toUpperCase()}
                        </span>
                      ) : null}
                    </div>
                  </div>
                </div>

                <Space className="flex-shrink-0">
                  {isPreviewable(file) ? (
                    <Tooltip title="مشاهده">
                      <Button
                        type="text"
                        icon={<EyeOutlined />}
                        className="text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20"
                        onClick={() => handlePreview(file)}
                      />
                    </Tooltip>
                  ) : null}
                  <Tooltip title="دانلود">
                    <Button
                      type="text"
                      icon={<DownloadOutlined />}
                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                      onClick={() => handleDownload(file)}
                    />
                  </Tooltip>
                  <Popconfirm
                    title="حذف فایل"
                    description="آیا از حذف این فایل اطمینان دارید؟"
                    onConfirm={async () => {
                      await onDelete(file.id);
                      message.success('حذف شد');
                    }}
                    okText="بله، حذف کن"
                    cancelText="لغو"
                    okButtonProps={{ danger: true }}
                  >
                    <Tooltip title="حذف">
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        className="hover:bg-red-50 dark:hover:bg-red-900/20"
                      />
                    </Tooltip>
                  </Popconfirm>
                </Space>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Modal
        open={uploadModalOpen}
        closable={false}
        maskClosable={false}
        keyboard={false}
        footer={null}
        centered
        width={440}
        title={<span style={{ fontFamily: 'CustomFont' }}>در حال آپلود فایل</span>}
        styles={{ body: { fontFamily: 'CustomFont', paddingTop: 8 } }}
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-300 m-0 leading-6">
            لطفاً تا پایان آپلود صبر کنید.
          </p>
          <div className="rounded-xl border border-teal-100 dark:border-teal-900/40 bg-teal-50/50 dark:bg-teal-950/20 px-4 py-3">
            <div className="flex items-center justify-between gap-2 mb-2 text-sm text-gray-700 dark:text-gray-200">
              <span>
                {uploadProgress?.total > 1
                  ? `فایل ${(uploadProgress?.index ?? 0) + 1} از ${uploadProgress.total}`
                  : 'در حال ارسال'}
              </span>
              <span className="font-semibold tabular-nums shrink-0">
                {uploadProgress?.percent != null ? `${uploadProgress.percent}٪` : '۰٪'}
              </span>
            </div>
            <Progress
              percent={uploadProgress?.percent ?? 0}
              showInfo={false}
              status={uploading ? 'active' : 'success'}
              strokeColor={{ from: '#14b8a6', to: '#22c55e' }}
            />
            {uploadProgress?.fileName ? (
              <div
                className="mt-2 text-xs text-gray-500 dark:text-gray-400 truncate"
                title={uploadProgress.fileName}
              >
                {uploadProgress.fileName}
              </div>
            ) : null}
          </div>
          {uploading ? (
            <div className="flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <Spin size="small" />
              <span>در حال ارسال به سرور...</span>
            </div>
          ) : null}
        </div>
      </Modal>

      <Modal
        open={convertModalOpen}
        onCancel={() => {
          if (uploading) return;
          setConvertModalOpen(false);
          setPendingFiles(null);
        }}
        footer={null}
        centered
        destroyOnClose
        title={<span style={{ fontFamily: 'CustomFont' }}>تبدیل فایل</span>}
        styles={{ body: { fontFamily: 'CustomFont' } }}
      >
        <p className="text-gray-700 dark:text-gray-200 text-base mb-6 leading-7">
          آیا فایل نیاز به تبدیل دارد؟
        </p>
        <div className="flex flex-wrap justify-end gap-2">
          <Button
            disabled={uploading}
            onClick={() => {
              setConvertModalOpen(false);
              setPendingFiles(null);
            }}
          >
            انصراف
          </Button>
          <Button
            disabled={!pendingFiles?.length}
            onClick={() => runUpload(pendingFiles, false)}
          >
            خیر
          </Button>
          <Button
            type="primary"
            disabled={!pendingFiles?.length}
            className="bg-indigo-600 hover:bg-indigo-700 border-0"
            onClick={() => runUpload(pendingFiles, true)}
          >
            بله
          </Button>
        </div>
      </Modal>

      <Modal
        open={previewVisible}
        onCancel={handleClosePreview}
        zIndex={1200}
        footer={
          previewFile ? (
            <div className="flex items-center justify-between">
              <span
                className="text-sm text-gray-500 truncate max-w-[60%]"
                style={{ fontFamily: 'CustomFont' }}
              >
                {previewFile.filename || previewFile.name || 'فایل بدون نام'}
              </span>
              <Space>
                {getFileUrl(previewFile) ? (
                  <Button
                    onClick={() =>
                      window.open(getFileUrl(previewFile), '_blank', 'noopener,noreferrer')
                    }
                    style={{ fontFamily: 'CustomFont' }}
                  >
                    باز کردن در تب جدید
                  </Button>
                ) : null}
                <Button
                  icon={<DownloadOutlined />}
                  onClick={() => handleDownload(previewFile)}
                  style={{ fontFamily: 'CustomFont' }}
                >
                  دانلود
                </Button>
                <Button onClick={handleClosePreview} style={{ fontFamily: 'CustomFont' }}>
                  بستن
                </Button>
              </Space>
            </div>
          ) : null
        }
        title={
          <div className="flex items-center gap-2">
            <EyeOutlined className="text-blue-500" />
            <span style={{ fontFamily: 'CustomFont' }}>مشاهده فایل</span>
          </div>
        }
        width={800}
        style={{ top: 20, fontFamily: 'CustomFont' }}
        destroyOnClose
        styles={{ body: { padding: 16, minHeight: 200 } }}
      >
        {renderPreviewContent()}
      </Modal>
    </div>
  );
};

export default WorkFilePanel;
